import React, { useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import LaptopIcon from "@material-ui/icons/Laptop";
import PhoneIcon from "@material-ui/icons/StayCurrentPortrait";
import SettingsIcon from "@material-ui/icons/Settings";
import Button from "@material-ui/core/Button";
import { DEVICES, TABS } from "../../constants";
import { WebpageCreationContext } from "../..";
import { useDispatch, useSelector } from "react-redux";
import { is_empty } from "utils/validations";
import { getRAUrlParams } from "features/Common/utils/url.utils";
import {
  GJS_HOMEPAGE_BUILDER_ROUTE_GENERATORS,
  GJS_HOMEPAGE_BUILDER_ROUTE_KEYS,
  HOMEPAGE_APIS,
  HOMEPAGE_BUILDER_SESSION_KEYS,
  HOMEPAGE_SETTINGS_ROUTE_PATH,
  HOMEPAGE_URL_QUERY_PARAMS,
  HOMEPAGE_BUILDER_MODE_VALUES,
} from "features/HomepageBuilder/constants/HomepageBuilder.constants";
import { app_route_ids, app_route_keys } from "constants/urlPaths";
import {
  EXLY_WEBPAGE_DETAILS__SET_SAVE_CTA,
  EXLY_WEBPAGE_DETAILS__SET_REORDERING_TYPE,
} from "../../redux/actions";
import { dataProvider } from "data";
import { baseApiClient } from "data/modules/api/api.client";
import { logError } from "utils/error";
import { orgPermissions, RESOURCE_KEYS } from "utils/OrgPermissions";
import { useCustomizationStore } from "features/ManageWebpage/modules/Customizations/store/useCustomizationStore";
import { ArrowBack } from "@material-ui/icons";
import { SavingStatus } from "features/ManageOfferings/modules/SavingStatus/SavingStatus";
import { useDesktopMediaQuery } from "features/Common/hooks/useDesktopMediaQuery";

const TEMPLATE_PREVIEW_ROUTE = HOMEPAGE_SETTINGS_ROUTE_PATH;
const APP_HOME_ROUTE = app_route_ids[app_route_keys.app_home];

const Navbar = (props) => {
  const {
    handleSave,
    handlePreview,
    device,
    setDevice,
    disableAllEdits,
    isCustomTabMode,
  } = props;

  const save_cta_text = useSelector(
    (state) => state.webpage_detail.save_cta_text
  );
  const handleSaveCta = useSelector(
    (state) => state.webpage_detail.handleSaveCta
  );
  const selected_reordering_type = useSelector(
    (state) => state.webpage_detail.selected_reordering_type
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const showTemplates =
    !orgPermissions.isNavigationItemHidden(
      RESOURCE_KEYS.SUBSECTIONS.MANAGE_WEBPAGE.CHANGE_TEMPLATE,
      RESOURCE_KEYS.SECTIONS.MANAGE_WEBPAGE
    ) && !disableAllEdits;

  const navbarRef = useRef(null);

  useEffect(() => {
    const navbarHeight = navbarRef.current?.offsetHeight ?? 0;

    const root = document.querySelector(":root");
    root.style.setProperty("--navbar-height", `${navbarHeight + 8}px`);
  }, []);

  const {
    sidebar_active_tab,
    set_sidebar_active_tab,
    is_settings_view,
    set_is_settings_view,
  } = useContext(WebpageCreationContext);

  const isDesktop = useDesktopMediaQuery();
  const isMobileView = !isDesktop || device === DEVICES.MOBILE;

  const is_reordering = sidebar_active_tab === TABS.REORDER;

  const isAdvancedBuilder =
    getRAUrlParams().get(HOMEPAGE_URL_QUERY_PARAMS.builder) ===
    HOMEPAGE_BUILDER_MODE_VALUES.advanced;

  const isClassicBuilderRoute = () =>
    window.location.hash.split("?")[0].replace(/\/$/, "") ===
    "#/TemplatePreview";

  const handleBack = () => {
    if (is_reordering && selected_reordering_type) {
      dispatch({
        type: EXLY_WEBPAGE_DETAILS__SET_SAVE_CTA,
        save_cta_text: "",
        handleSaveCta: null,
      });
      dispatch({
        type: EXLY_WEBPAGE_DETAILS__SET_REORDERING_TYPE,
        selected_reordering_type: null,
      });
      return;
    }
    if (isAdvancedBuilder) {
      handleBackToAdvancedBuilder();
      return;
    }
    if (is_reordering) {
      if (isClassicBuilderRoute()) {
        set_is_settings_view(false);
        set_sidebar_active_tab(TABS.MY_PAGE);
        return;
      }
      history.replace(TEMPLATE_PREVIEW_ROUTE);
      return;
    }
    // plain editor view - return to the app dashboard
    history.replace(APP_HOME_ROUTE);
  };

  const handleBackToAdvancedBuilder = async () => {
    // settings opened from the builder in this session - the builder entry is
    // right below in router history, so a single goBack lands on it exactly
    if (sessionStorage.getItem(HOMEPAGE_BUILDER_SESSION_KEYS.opened_settings)) {
      sessionStorage.removeItem(HOMEPAGE_BUILDER_SESSION_KEYS.opened_settings);
      history.goBack();
      return;
    }
    try {
      const response = await baseApiClient.get(
        HOMEPAGE_APIS.get_active_homepage
      );
      const homepage = response?.data?.data?.homepage;
      if (is_empty(homepage?.uuid)) {
        history.replace(APP_HOME_ROUTE);
        return;
      }
      history.replace(
        GJS_HOMEPAGE_BUILDER_ROUTE_GENERATORS[
          GJS_HOMEPAGE_BUILDER_ROUTE_KEYS.update
        ]({
          homepageUuid: homepage.uuid,
          templateUuid:
            homepage?.metadata?.template_uid || homepage?.template?.slug,
        })
      );
    } catch (err) {
      logError({
        error: err,
        occuredAt: "src/ui/pages/TemplatePreview/components/Navbar/index.js",
        when: "navigating back to advanced builder",
      });
      history.replace(APP_HOME_ROUTE);
    }
  };

  const { activeReorderTab } = useCustomizationStore();

  return (
    <div
      ref={navbarRef}
      className="tw-border tw-relative tw-border-[#ddd] tw-py-[12px] tw-px-[24px] tw-flex tw-flex-row tw-justify-center tw-items-center tw-font-bold tw-text-lg"
    >
      <div className="tw-flex tw-flex-row tw-justify-center tw-items-center">
        <div
          onClick={handleBack}
          className="tw-cursor-pointer hover:tw-text-[#3f51b5]"
        >
          <ArrowBack className="tw-mb-[3px] tw-opacity-100 tw-transition-opacity tw-duration-200 hover:tw-opacity-70" />
        </div>

        {activeReorderTab ? (
          <div className="tw-text-[#272522] tw-ml-3">
            {activeReorderTab}&nbsp;/&nbsp;
            <span className="tw-text-[#493AB1]">Setup</span>
          </div>
        ) : is_settings_view ? (
          <div className="tw-text-[18px] tw-py-[9px] tw-pl-[12px] tw-pr-[18px] tw-capitalize tw-whitespace-pre">
            Settings
          </div>
        ) : isCustomTabMode ? (
          <div className="tw-text-[18px] tw-py-[9px] tw-pl-[12px] tw-pr-[18px] tw-capitalize tw-whitespace-pre">
            Settings
          </div>
        ) : (
          <div
            onClick={handleBack}
            className="tw-cursor-pointer tw-text-[18px] tw-py-[9px] tw-pl-[12px] tw-pr-[18px] tw-capitalize tw-whitespace-pre"
          >
            {isMobileView ? "Exit" : "Exit Homepage Editing"}
          </div>
        )}
      </div>
      <div className="tw-flex-grow" />

      {!is_reordering && (
        <div
          className="tw-absolute tw-left-1/2  tw-top-[12px]  tw-z-10 tw-hidden sm:tw-flex tw-flex-row tw-items-center tw-justify-center tw-bg-[#f2f2f7] tw-rounded-[24px] tw-p-[4px] -tw-translate-x-1/2"
          title={`Change view to ${device.toLowerCase()}`}
        >
          <div
            className={`tw-px-[18px] tw-py-[6px] tw-cursor-pointer tw-text-[#2e2e2e] tw-rounded-[20px] tw-border-none tw-transition-colors ${
              device === DEVICES.MOBILE
                ? "tw-bg-white tw-text-[#493AB1] tw-shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
                : ""
            }`}
            onClick={() => {
              setDevice(DEVICES.MOBILE);
            }}
          >
            <PhoneIcon className="tw-text-inherit" />
          </div>
          <div
            className={`tw-px-[18px] tw-py-[6px] tw-cursor-pointer tw-text-[#2e2e2e] tw-rounded-[20px] tw-border-none tw-transition-colors ${
              device === DEVICES.DESKTOP
                ? "tw-bg-white tw-text-[#493AB1] tw-shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
                : ""
            }`}
            onClick={() => {
              setDevice(DEVICES.DESKTOP);
            }}
          >
            <LaptopIcon className="tw-text-inherit" />
          </div>
        </div>
      )}

      <div className="tw-flex tw-flex-row tw-justify-center tw-items-center sm:tw-flex-row">
        {!is_reordering && (
          <>
            {showTemplates && (
              <a
                href={`/#/Templates`}
                className="tw-text-[#493AB1] tw-font-medium tw-mx-4 tw-whitespace-pre tw-no-underline hover:tw-text-[#493AB1]"
                onClick={() => {
                  dataProvider.postAnalyticalEvent(
                    "templateSelection",
                    "changeTemplateClick",
                    {
                      flow: "templateSelection",
                      funnel: "templateSelection",
                      ui_component: "Text Button",
                      identifier: "Navbar",
                    }
                  );
                }}
              >
                Change Template
              </a>
            )}
            {!isCustomTabMode && !isMobileView && (
              <Button
                variant="text"
                disableElevation
                className="!tw-normal-case !tw-font-medium !tw-border !tw-border-solid !tw-border-[#493AB1] !tw-text-[#493AB1] !tw-px-4 !tw-py-2"
                startIcon={<SettingsIcon className="!tw-text-[#493AB1]" />}
                onClick={() => {
                  set_is_settings_view(true);
                  set_sidebar_active_tab(TABS.REORDER);
                }}
              >
                Settings
              </Button>
            )}
          </>
        )}

        {is_reordering ? (
          <div className="tw-flex tw-flex-row tw-gap-6 tw-items-center sm:tw-ml-6">
            <SavingStatus customStore={useCustomizationStore} />
            <Button
              variant="text"
              color="primary"
              disableElevation
              className="!tw-normal-case !tw-font-medium !tw-hidden md:!tw-flex"
              onClick={() => handlePreview()}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              disabled={disableAllEdits}
              className="!tw-normal-case !tw-hidden md:!tw-flex"
              onClick={() => {
                handleSaveCta
                  ? handleSaveCta()
                  : handleSave("Webpage Saved Successfully!");
              }}
            >
              {is_empty(save_cta_text) ? "Save changes" : save_cta_text}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

Navbar.propTypes = {
  handleSave: PropTypes.func,
  handlePreview: PropTypes.func,
  subDomain: PropTypes.string,
  handleSubdomainChange: PropTypes.func,
  device: PropTypes.oneOf([DEVICES.MOBILE, DEVICES.DESKTOP]),
  setDevice: PropTypes.func,
};

export { Navbar };
